import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { API, fileUrl } from "@/api";
import axios from "axios";

const Ctx = createContext(null);
export const usePlayer = () => useContext(Ctx);

export const trackAudioUrl = (t) => {
  if (!t) return null;
  if (t.audio_url) return t.audio_url;
  if (t.audio_path) return fileUrl(t.audio_path);
  return null;
};
export const trackCoverUrl = (t) => {
  if (!t) return null;
  if (t.cover_url) return t.cover_url;
  if (t.cover_path) return fileUrl(t.cover_path);
  return null;
};

export default function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.9);

  const current = index >= 0 ? queue[index] : null;

  const playList = useCallback((tracks, startIdx = 0) => {
    setQueue(tracks);
    setIndex(startIdx);
    setPlaying(true);
  }, []);

  const playTrack = useCallback((track, list = null) => {
    const q = list && list.length ? list : [track];
    const i = q.findIndex((t) => t.id === track.id);
    setQueue(q);
    setIndex(i >= 0 ? i : 0);
    setPlaying(true);
  }, []);

  const toggle = useCallback(() => {
    if (!current) return;
    setPlaying((p) => !p);
  }, [current]);

  const next = useCallback(() => {
    if (index < queue.length - 1) setIndex(index + 1);
  }, [index, queue.length]);

  const prev = useCallback(() => {
    if (index > 0) setIndex(index - 1);
  }, [index]);

  const seek = useCallback((pct) => {
    const el = audioRef.current;
    if (el && el.duration) el.currentTime = (pct / 100) * el.duration;
  }, []);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const url = trackAudioUrl(current);
    if (!url) return;
    el.src = url;
    el.volume = volume;
    if (playing) el.play().catch((err) => { if (err?.name !== "AbortError") setPlaying(false); });
    // Fire play count
    if (current?.id && !current?.is_demo) {
      axios.post(`${API}/tracks/${current.id}/play`).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) el.play().catch((err) => { if (err?.name !== "AbortError") setPlaying(false); });
    else el.pause();
  }, [playing]);

  useEffect(() => {
    const el = audioRef.current;
    if (el) el.volume = volume;
  }, [volume]);

  return (
    <Ctx.Provider value={{
      queue, index, current, playing, progress, duration, volume,
      playTrack, playList, toggle, next, prev, seek, setVolume,
    }}>
      {children}
      <audio
        ref={audioRef}
        onTimeUpdate={(e) => {
          const el = e.currentTarget;
          setProgress(el.duration ? (el.currentTime / el.duration) * 100 : 0);
        }}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onEnded={next}
        preload="metadata"
      />
    </Ctx.Provider>
  );
}
